import React from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { Box, Typography, Stack, Tabs, Tab } from '@mui/material';
import ScienceRounded from '@mui/icons-material/ScienceRounded';

import InkStockView from './InkStockView';
import InkBalanceView from './InkBalanceView';
import InkPurchaseOrdersView from './InkPurchaseOrdersView';
import InkCatalogueView from './InkCatalogueView';
import { TOKENS } from './board/ganttConfig';

/**
 * The Ink shell — one nav entry, ordered by the question each tab answers,
 * operational first and master data last:
 *
 *   Balance    — what do we have vs. need?   (the daily screen: stock + demand,
 *                clickable to each SKU's batches and ledger; expand a row to see
 *                the jobs driving its demand; Goods-in, Opening balance and
 *                Adjust all live here)
 *   Orders     — what have we ordered?
 *   Catalogue  — what is it?                 (series × colour = the SKU)
 *   Suppliers  — who do we buy it from?
 *
 * Balance absorbed the old standalone "Stock on hand" and "Demand" tabs: their
 * columns, the ledger modal and the per-ink job breakdown all moved onto it, so
 * there's one stock-and-demand screen, not three.
 *
 * Assign, Sticker and Trolley stay as their own nav items on purpose: they are
 * things you *do* on the floor, not questions you ask about materials.
 *
 * The tab lives in the URL (/production/ink/:tab) rather than in component
 * state, so a tab is linkable, survives a refresh, and the browser Back button
 * behaves. The sidebar entry prefix-matches (`matchPrefix` in productionRoutes)
 * so it stays lit on any tab.
 */

const TABS = [
  { value: 'balance', label: 'Balance' },
  { value: 'orders', label: 'Purchase orders' },
  { value: 'catalogue', label: 'Catalogue' },
  { value: 'suppliers', label: 'Suppliers' },
] as const;

type InkTab = typeof TABS[number]['value'];

const BLURB: Record<InkTab, string> = {
  balance:
    'Every SKU on one line: what we hold, what it’s worth, what’s on order, what jobs '
    + 'demand, and the balance left. Anything short of its reorder point floats to the top — '
    + 'your order list. Click a row for its batches and full ledger history.',
  orders:
    'Purchase orders raised against a supplier. Build a draft, issue it to place the '
    + 'order, then receive stock against its lines on delivery — which draws the order '
    + 'down and lands the batch in the store.',
  catalogue:
    'The materials master. Series define the key suffix; a product is colour × series = the '
    + 'unique ink key. Purchasing lives on the product: supplier, their article number, pack '
    + 'size, standard cost and reorder point.',
  suppliers:
    'Who we buy ink from. A series carries the default supplier for everything under it; an '
    + 'individual product can override that.',
};

export default function InkMaterialsView() {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab: string }>();

  const isTab = (v: string | undefined): v is InkTab =>
    !!v && TABS.some((t) => t.value === v);

  // An unknown tab (including the retired /stock) shouldn't 404 into a blank
  // page — send it to the default.
  if (!isTab(tab)) return <Navigate to="/production/ink/balance" replace />;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
        <ScienceRounded sx={{ color: TOKENS.accent }} />
        <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Ink</Typography>
      </Stack>
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2, maxWidth: 900 }}>
        {BLURB[tab]}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_e, v: InkTab) => navigate(`/production/ink/${v}`)}
        sx={{
          mb: 2.5, minHeight: 36, borderBottom: '1px solid rgba(0,0,0,0.08)',
          '& .MuiTab-root': { minHeight: 36, fontSize: 13, fontWeight: 700, textTransform: 'none' },
          '& .Mui-selected': { color: `${TOKENS.accent} !important` },
          '& .MuiTabs-indicator': { bgcolor: TOKENS.accent },
        }}
      >
        {TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
      </Tabs>

      {tab === 'suppliers' && <InkStockView embedded section="suppliers" />}
      {tab === 'balance' && <InkBalanceView embedded />}
      {tab === 'orders' && <InkPurchaseOrdersView embedded />}
      {tab === 'catalogue' && <InkCatalogueView embedded />}
    </Box>
  );
}
